/**
 * AssistantSelect Component
 * A specialized version of UserSelectDropdown for selecting assistants
 */

import React, { FC, memo, useMemo } from 'react';
import UserSelectDropdown from '../UserSelectDropdown/UserSelectDropdown';
import { UserOption, buildUserOptions } from '../../../utils/helpers/user/userOptionsBuilder';
import { USER_FORMAT_PRESETS } from '../../../utils/helpers/text/userFormatting';

interface AssistantSelectProps {
  selectedAssistantId: string;
  users: any[];
  loading?: boolean;
  onAssistantChange: (userId: string, displayName: string) => void;
  required?: boolean;
  disabled?: boolean;
  width?: number | string;
  style?: React.CSSProperties;
}

/**
 * AssistantSelect - Dropdown for selecting an assistant user
 */
const AssistantSelect: FC<AssistantSelectProps> = memo(({
  selectedAssistantId,
  users,
  loading = false,
  onAssistantChange,
  required = true,
  disabled = false,
  width = 200,
  style = {}
}) => {
  const handleChange = (selected: UserOption | null, userData?: any) => {
    if (selected && selected.name) {
      onAssistantChange(selected.name, selected.label);
    } else {
      onAssistantChange("", "");
    }
  };

  const optionsConfig = useMemo(() => ({
    formatConfig: USER_FORMAT_PRESETS.default,
    includeAllUsers: false, // No "All users" option for assistants
    enableSort: true
  }), []);

  const options = useMemo(() => {
    if (!users || users.length === 0) return [];
    return buildUserOptions(users, optionsConfig);
  }, [users, optionsConfig]);

  const handleSelectChangeByIndex = (ev: any) => {
    if (ev.detail !== null && ev.detail !== undefined) {
      const selectedOption = options[ev.detail];
      if (selectedOption) {
        handleChange(selectedOption);
      }
    } else if (!required) {
      handleChange(null);
    }
  };

  // Value can be the ID or the full user object
  const currentValue = selectedAssistantId || null;

  return (
    <UserSelectDropdown
      value={currentValue}
      users={users}
      onChange={handleChange}
      optionsConfig={optionsConfig}
      label="Select assistant"
      placeholder="Type to search users"
      required={required}
      disabled={disabled}
      loading={loading}
      loadingText="Loading assistants..."
      width={width}
      style={style}
      dataTestid="assistant-select"
      name="assistantName"
      clearable={!required}
      returnUserData={true}
      dropdownProps={{ onSelectChange: handleSelectChangeByIndex }}
    />
  );
});

AssistantSelect.displayName = 'AssistantSelect';

export default AssistantSelect;
